import { useEffect, useState } from 'react';

/**
 * useAuthToken
 * - Parses `token` from URL query string and stores it in localStorage as `userToken`.
 * - If not present in URL, falls back to existing `userToken` from localStorage.
 * - Returns the token and a ready flag so UI can wait until initial parse completes.
 */
export function useAuthToken() {
  const [userToken, setUserToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tokenFromUrl = params.get('token');

      if (tokenFromUrl && tokenFromUrl.trim().length > 0) {
        localStorage.setItem('userToken', tokenFromUrl);
        setUserToken(tokenFromUrl);

        // Clean the token from the URL for security (preserve other params)
        params.delete('token');
        const newSearch = params.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
        // Replace state to avoid adding a new history entry
        window.history.replaceState({}, document.title, newUrl);
      } else {
        const existing = localStorage.getItem('userToken');
        if (existing) setUserToken(existing);
      }
    } finally {
      setReady(true);
    }
  }, []);

  return { userToken, setUserToken, ready } as const;
}

export default useAuthToken;
